import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/common/ConfirmDialog";
import {
  useGetConnectionCredentialTypesQuery,
  type ConnectionCredentialSlot,
} from "features/integrationsBeta/connections";
import {
  CredentialItem,
  useDeleteSecretMutation,
  useGetCredentialsQuery,
  useLazyGetSecretQuery,
} from "features/integrationsBeta/credentials";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useState } from "react";
import PayloadView from "./PayloadView";
import SecretDialog from "./SecretDialog";

/**
 * Slot-based credential management. The provider adapter declares which
 * credential types a connection can hold (fixed AWS Secrets Manager
 * schemas) - the UI renders exactly those slots. There is no way to add a
 * credential of an arbitrary type or with arbitrary fields.
 */

type SlotRow =
  | {
      kind: "slot";
      slot: ConnectionCredentialSlot;
      credential: CredentialItem | null;
    }
  | { kind: "orphan"; credential: CredentialItem };

function RevealBlock({
  connectionId,
  type,
}: {
  connectionId: number;
  type: string;
}) {
  const [fetchSecret, { data: secretData, isFetching }] =
    useLazyGetSecretQuery();
  const [revealed, setRevealed] = useState(false);

  // Defense in depth: rows are keyed by slot type, so React may reuse this
  // instance across connections - never keep a reveal from another identity.
  useEffect(() => {
    setRevealed(false);
  }, [connectionId, type]);

  const toggleReveal = async () => {
    if (revealed) {
      setRevealed(false);
      return;
    }
    // Always re-fetch on reveal: guarantees the value belongs to THIS
    // connection/type and picks up out-of-band secret rotations.
    try {
      await fetchSecret({ connectionId, type }).unwrap();
    } catch {
      return;
    }
    setRevealed(true);
  };

  return (
    <>
      <Tooltip title={revealed ? "Hide secret" : "Reveal secret"}>
        <IconButton size="small" onClick={toggleReveal} disabled={isFetching}>
          {revealed ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Box sx={{ mt: 1, width: "100%", order: 10 }}>
        {revealed && secretData?.secret ? (
          <PayloadView
            value={secretData.secret}
            emptyHint="No secret fields"
            boxed
            title="Secret values"
            copyable
          />
        ) : (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            ••••••••
          </Typography>
        )}
      </Box>
    </>
  );
}

export default function CredentialsManager({
  connectionId,
}: {
  connectionId: number;
}) {
  const dispatch = useAppDispatch();
  const { canEdit } = usePlatformRole();
  const { data: credentials = [] } = useGetCredentialsQuery(connectionId);
  const { data: slotInfo, isError: slotsUnavailable } =
    useGetConnectionCredentialTypesQuery(connectionId);
  const [deleteSecret, { isLoading: deleting }] = useDeleteSecretMutation();
  const [dialog, setDialog] = useState<{
    mode: "add" | "edit";
    type: string;
    typeLabel: string;
    label?: string;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    type: string;
    label: string;
  } | null>(null);

  const credentialByType = new Map(credentials.map((c) => [c.type, c]));
  const allowedTypes = new Set(
    (slotInfo?.credential_types ?? []).map((slot) => slot.type),
  );

  // A required slot stops being "missing" once ANY member of its one-of
  // group is configured (e.g. SFTP needs SSH Key OR Login Password).
  const groupSatisfied = (slot: ConnectionCredentialSlot): boolean => {
    if (slot.required_group == null) return false;
    const group = slotInfo?.required_groups?.[slot.required_group] ?? [];
    return group.some((type) => credentialByType.has(type));
  };

  const rows: SlotRow[] = [
    ...(slotInfo?.credential_types ?? []).map(
      (slot): SlotRow => ({
        kind: "slot",
        slot,
        credential: credentialByType.get(slot.type) ?? null,
      }),
    ),
    // Credentials whose type the adapter no longer declares: legacy leftovers,
    // kept visible so they can be inspected and removed.
    ...credentials
      .filter((c) => slotInfo && !allowedTypes.has(c.type))
      .map((credential): SlotRow => ({ kind: "orphan", credential })),
  ];

  const handleDelete = async (type: string) => {
    try {
      await deleteSecret({ connectionId, type }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Credential deleted.",
        }),
      );
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not delete the credential.",
        }),
      );
    } finally {
      setConfirmDelete(null);
    }
  };

  const rowBoxSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    p: 1.5,
  } as const;
  const headSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    flexWrap: "wrap",
  } as const;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Credentials
      </Typography>

      <Stack spacing={1}>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {slotsUnavailable
              ? "Credential requirements unavailable."
              : slotInfo && !slotInfo.adapter_registered
                ? "No provider adapter is registered for this connection type — it cannot hold credentials."
                : "This connection type needs no credentials."}
          </Typography>
        )}

        {rows.map((row) => {
          if (row.kind === "orphan") {
            const cred = row.credential;
            return (
              <Box key={`orphan-${cred.id}`} sx={rowBoxSx}>
                <Box sx={headSx}>
                  <Typography variant="body2" fontWeight={600}>
                    {cred.type}
                    {cred.label ? ` — ${cred.label}` : ""}
                  </Typography>
                  <Chip
                    label="Not supported by this connection"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <RevealBlock connectionId={connectionId} type={cred.type} />
                  {canEdit && (
                    <Tooltip title="Delete credential">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setConfirmDelete({
                            type: cred.type,
                            label: cred.type,
                          })
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          }

          const { slot, credential } = row;
          const satisfiedByAlternative =
            slot.required && !credential && groupSatisfied(slot);
          const requiredChip = (
            <Chip
              label={
                satisfiedByAlternative
                  ? "Optional — alternative configured"
                  : slot.required
                    ? "Required"
                    : "Optional"
              }
              size="small"
              color={
                slot.required && !credential && !satisfiedByAlternative
                  ? "warning"
                  : "default"
              }
              variant="outlined"
            />
          );

          if (!credential) {
            return (
              <Box key={slot.type} sx={rowBoxSx}>
                <Box sx={headSx}>
                  <Typography variant="body2" fontWeight={600}>
                    {slot.label}
                  </Typography>
                  {requiredChip}
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Not configured
                  </Typography>
                  {canEdit && (
                    <Button
                      size="small"
                      onClick={() =>
                        setDialog({
                          mode: "add",
                          type: slot.type,
                          typeLabel: slot.label,
                        })
                      }
                    >
                      Configure
                    </Button>
                  )}
                </Box>
              </Box>
            );
          }

          return (
            <Box key={slot.type} sx={rowBoxSx}>
              <Box sx={headSx}>
                <Typography variant="body2" fontWeight={600}>
                  {slot.label}
                  {credential.label ? ` — ${credential.label}` : ""}
                </Typography>
                {requiredChip}
                <Box sx={{ flexGrow: 1 }} />
                <RevealBlock connectionId={connectionId} type={slot.type} />
                {canEdit && (
                  <>
                    <Tooltip title="Edit credential">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setDialog({
                            mode: "edit",
                            type: slot.type,
                            typeLabel: slot.label,
                            label: credential.label,
                          })
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete credential">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setConfirmDelete({
                            type: slot.type,
                            label: slot.label,
                          })
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>

      {dialog && (
        <SecretDialog
          open
          onClose={() => setDialog(null)}
          connectionId={connectionId}
          mode={dialog.mode}
          type={dialog.type}
          typeLabel={dialog.typeLabel}
          label={dialog.label}
        />
      )}

      <ConfirmDialog
        open={confirmDelete != null}
        title="Delete credential"
        message={`Delete the "${confirmDelete?.label ?? ""}" credential? The secret is removed from AWS Secrets Manager. This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={() => confirmDelete && handleDelete(confirmDelete.type)}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
